<script setup lang="ts">
import type { FormError, FormSubmitEvent } from '#ui/types'

const emit = defineEmits<{ close: [boolean] }>()

const newOpportunitiesAlertModalOpen = useRouteQuery('alert', true)

const config = useRuntimeConfig()
const queryCoordinates = useRouteQuery('coordinates', null)
const queryValue = useRouteQuery('value', null)
const queryActivity = useRouteQuery('activity', null)
const queryDomain = useRouteQuery('domain', null)
const queryUTMCampaign = useRouteQuery('utm_campaign', null)
const platform = useState('platform')

const step = ref('form')
const cta = ref('close')
if (platform.value.slug === 'benevolt') { //TODO: check if login too
  cta.value = 'activate-account'
}

function setStep(value: string) {
  step.value = value
}

const form = ref()
const state = reactive({
  firstname: '',
  lastname: '',
  email: '',
  location: null,
})

if (queryCoordinates.value && queryValue.value) {
  state.location = {
    coordinates: queryCoordinates.value,
    value: queryValue.value,
  }
}

async function onSubmit(event: FormSubmitEvent<any>) {
  await $api(`/saved-search`, {
    method: 'POST',
    body: {
      firstname: event.data.firstname,
      lastname: event.data.lastname,
      email: event.data.email,
      location: {
        value: event.data.location?.value,
        coordinates: {
          lat: event.data.location?.coordinates?.split(',')[0],
          lng: event.data.location?.coordinates?.split(',')[1],
        }
      },
      source: queryUTMCampaign.value,
      activity: queryActivity.value,
      domain: queryDomain.value,
      utm_source: useCookie('utm_source').value,
      utm_campaign: useCookie('utm_campaign').value,
    },
    async onResponseError({ response }) {
      await defaultOnResponseError(response)
      if (response.status === 422) {
        form.value.setErrors(formatErrors(response._data.errors));
      } else if (response.status === 403) {
        step.value = 'account-error'
      }
    },
    async onResponse({ response }) {
      if (response.ok) {
        step.value = 'success'
      }
    },
  })

}

function onCloseModal() {
  newOpportunitiesAlertModalOpen.value = undefined
}
</script>

<template>
  <UModal
      title="Ne passez pas à côté des futures missions !"
      :close="{onClick: () => onCloseModal()}"
  >
    <template #body>
      <UForm :state="state" @submit="onSubmit" ref="form" class="space-y-4" v-if="step == 'form'">
        <div class="grid md:grid-cols-2 gap-4">
          <UFormField label="Prénom" name="firstname" required>
            <UInput v-model="state.firstname" placeholder="Camille" class="w-full" />
          </UFormField>
          <UFormField label="Nom" name="lastname" required>
            <UInput v-model="state.lastname" placeholder="Dupont" class="w-full" />
          </UFormField>
        </div>
        <UFormField label="Email" name="email" required>
          <UInput v-model="state.email" placeholder="camille.dupont@mail.com" class="w-full" />
        </UFormField>
        <UFormField label="Ville" name="location" required>
          <InputCity v-model="state.location" class="w-full" />
        </UFormField>
        <p class="text-xs text-justify text-muted" v-if="platform.simplfied_group?.network?.settings?.GDPRFormInfo" v-html="platform.simplfied_group.network.settings.GDPRFormInfo" />
        <UButton type="submit" label="Créer mon alerte" size="xl" icon="i-ph-bell-duotone" block loading-auto />
        <p class="text-xs text-justify text-muted">En cliquant sur « Créer mon alerte », je reconnais <span v-if="platform?.slug === 'benevolt'">avoir plus de 18 ans,</span> avoir pris connaissance des <NuxtLink class="underline" href="/cgu" target="_blank">Conditions Générales d’Utilisation</NuxtLink> et de la <NuxtLink class="underline" href="/politique-donnees" target="_blank">Politique de protection des données</NuxtLink> du site et les accepter.</p>
      </UForm>
      <div v-if="step == 'success'" class="text-center space-y-4">
        <img src="/im/emoji/thumbs-up.png" class="mx-auto">

        <p class="text-2xl font-semibold">Super !</p>
        <p class="text-lg">
          Vous serez alerté par email lorsqu'une<br>mission de bénévolat sera disponible.
        </p>

        <div class="u-inline-center">
          <UButton size="lg" v-if="cta == 'activate-account'" :to="`/se-connecter?email=${encodeURIComponent(state.email)}`" external>Activer mon compte</UButton>
          <UButton size="lg" v-else @click="emit('close', true)">Fermer</UButton>
        </div>
      </div>
      <div v-if="step == 'account-error'" class="text-center space-y-4">
        <img src="/im/emoji/thinking-face.png" class="mx-auto">

        <h1 class="text-2xl font-semibold">Oups</h1>
        <h2 class="text-lg">Impossible d'effectuer cette action</h2>

        <p class="text-lg">
          Cet email ne fonctionne pas. Vous pouvez utilisez une autre adresse email.
        </p>

        <UButton size="lg" @click="setStep('form')">Retour</UButton>
      </div>
    </template>
  </UModal>
</template>